import { FormStepper } from "@common/components/stepper/FormStepper"
import TextM from "@common/components/text/TextM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { congratsTypes } from "@common/screens/CongratsScreen"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import { weekDayNums } from "@data/constants/activitiesConstants"
import {
    sectionStudentClasses,
    studentClasses,
    subjectPriorities,
} from "@data/constants/formConstants"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { putCongrats } from "@data/redux/actions/navigation"
import { tutorsService } from "@data/services/tutorsService"
import { validation } from "@data/utility/validation"
import React, { useState } from "react"
import { View } from "react-native"
import { StatusBar } from "@common/systemBars/StatusBar"
import { Checkbox } from "react-native-paper"
import WebView from "react-native-webview"
import { connect } from "react-redux"
import { SwitchComponent } from "../application/personalInfos/ApplicationPersonalInfos"
import ForeignLanguageStep from "../application/personalInfos/ForeignLanguageStep"
import TutorProfilePersonalizer from "./components/TutorProfilePersonalizer"
import { TutorAvailabilityStep } from "./TutorAvailabilityStep"

const CompleteTutorProfileStepper = (props) => {
    const { navigation } = props

    // FormStepper snapshots its defaultValues ONCE, at mount, and nothing
    // re-seeds them — so rendering before the user lands locks every prefilled
    // answer to empty for the whole session. Gate on the user, not on `profile`:
    // a tutor-candidate with no TutorProfile row legitimately has none.
    const isUserLoaded = !!props.user?.id

    const [formValues, setFormValues] = useState(null)
    const defaultValues = {}
    const [isAutoEntrepreneur, setIsAutoEntrepreneur] = useState(
        props.user?.profile?.num_SIRET !== null
    )
    const [charterOptIn, setCharterOptIn] = useState(false)
    const [continuityVisible, setContinuityVisible] = useState(false)

    // The step posts the OPEN créneaux only; the ENGAGED ones are the backend's to
    // keep. Both rules below still count the WHOLE declared week: a tutor whose
    // single créneau now hosts a live has not emptied their dispo, and asking them
    // for a new one before they may save any other profile field is a blocker.
    const engagedSlots = (props.weekView?.slots ?? [])
        .filter((s) => s.engaged)
        .map((s) => s.slot)
    const availabilitySlots = [
        ...(formValues?.availability?.slots ?? []),
        ...engagedSlots,
    ]
    // The créneaux alone answer the step: the save stamps the week, and a stamp now
    // carries its own proposal floor when the field is empty or seeded with a date
    // from a past season. Demanding the date here held a tutor coming back months
    // later out of their WHOLE profile, for an answer the save can give.
    const isAvailabilityStepAnswered = availabilitySlots.length > 0

    // Calculate if the following rule is respected : at least three slots, or, if two slots, they must be at least 1 day apart calculate with moment.js
    const isAvailabilitySlotsValid =
        availabilitySlots.length >= 3 ||
        (availabilitySlots.length === 2 &&
            Math.abs(
                weekDayNums[availabilitySlots[0]?.split(" ")[0]] -
                    weekDayNums[availabilitySlots[1]?.split(" ")[0]]
            ) !== 1)

    const formData = [
        {
            label: "Es-tu prêt.e à réaliser des missions fixes de tutorat ?",
            description:
                "Tu retrouveras tes élèves chaque semaine aux mêmes horaires",
            name: "fixed_missions",
            type: "select.single",
            options: [
                { label: "Oui", value: true },
                { label: "Non", value: false },
            ],
            defaultValue:
                props.user?.profile?.fixed_missions === null
                    ? null
                    : !!props.user?.profile?.fixed_missions,
            validationRules: { ...validation.required() },
        },
        {
            label: "Es-tu prêt.e à réaliser des missions de remplacement ?",
            description:
                "Tu seras appelé.e à remplacer ponctuellement d'autres tuteurs",
            name: "replace_missions",
            type: "select.single",
            options: [
                { label: "Oui", value: true },
                { label: "Non", value: false },
            ],
            defaultValue:
                props.user?.profile?.replace_missions === null
                    ? null
                    : !!props.user?.profile?.replace_missions,
            validationRules: { ...validation.required() },
        },
        {
            label: "Ajoute tes créneaux disponibles",
            name: "availability",
            type: "custom",
            defaultValue: {
                slots: props.user?.profile?.availability_slots ?? [],
                startingDate:
                    props.user?.profile?.availability_starting_date ?? null,
            },
            component: TutorAvailabilityStep,
            bottomComponent: !isAvailabilitySlotsValid && (
                <>
                    <TextM wrap>
                        Indique un maximum de créneaux où tu es disponible pour
                        donner cours, pour maximiser tes chances.
                    </TextM>
                </>
            ),
            showNextButton: true,
            // « Suivant » raises the engagement warning first; confirming it in
            // the sheet is what advances. On an unanswered step there is nothing
            // to commit to, so the tap falls through to the validation message
            // instead of being swallowed by a sheet.
            onNextButtonPress: () => {
                if (isAvailabilityStepAnswered) setContinuityVisible(true)
            },
            continueAfterCustomAction: !isAvailabilityStepAnswered,
            componentProps: {
                continuityVisible,
                onCloseContinuity: () => setContinuityVisible(false),
            },
            // `validateOnNext`, not `customCondition`: the latter also feeds the
            // « disabled » chain, where a predicate reads as permanently truthy
            // and would lock the Next button. This one runs on Next and blocks
            // with the message, which is what the old required rule did.
            validationRules: {
                validateOnNext: {
                    value: (value) =>
                        (value?.slots?.length ?? 0) + engagedSlots.length > 0,
                    message: "Ajoute au moins un créneau",
                },
            },
        },
        {
            label: "Quelles sont tes matières de spécialités ?",
            name: "specialty_school_subjects",
            type: "select.multiple",
            options: props.schoolSubjects
                ?.filter((ss) => ss?.is_specialty)
                ?.map((x) => {
                    return { icon: x?.icon, value: x.id, label: x.name }
                }),
            defaultValue:
                props.user?.specialty_school_subjects?.map((x) => x?.id) ?? [],
            validationRules: { ...validation.required() },
        },
        {
            label: "Quelles sont les langues étrangères que tu connais ?",
            name: "foreign_languages",
            type: "custom",
            component: (componentProps) => (
                <ForeignLanguageStep
                    {...componentProps}
                    schoolSubjects={props.schoolSubjects}
                />
            ),
            defaultValue:
                props.user?.foreign_languages?.map((x) => {
                    return {
                        id: x?.id,
                        name: x?.name,
                        proficiency: x?.proficiency,
                    }
                }) ?? [],
            validationRules: {
                validate: (value) => {
                    if (!value || value.length === 0) {
                        return true
                    }

                    const languagesWithoutProficiency = value.filter(
                        (lang) =>
                            lang.proficiency === undefined ||
                            lang.proficiency === null
                    )

                    if (languagesWithoutProficiency.length > 0) {
                        return "Veuillez sélectionner le niveau de maîtrise pour chaque langue"
                    }

                    return true
                },
            },
        },
        {
            label: "Tu te sens plutôt littéraire, scientifique ou les deux ?",
            name: "relevant_subjects",
            type: "select.multiple",
            options: subjectPriorities,
            defaultValue: props.user?.profile?.relevant_subjects,
            validationRules: { ...validation.required() },
        },
        {
            label:
                props.user?.profile?.admin_approved_classes?.length > 0
                    ? "Quelles classes souhaites-tu accompagner parmi celles approuvées ?"
                    : "Jusqu'a quel niveau te sens-tu capable d'accompagner des élèves sur tes matières de prédilection ?",
            name: "relevant_classes",
            type: "select.multiple",
            sectionMode: true,
            // If admin has approved classes, filter sections to only show approved classes
            // Otherwise show full sectioned list
            options: sectionStudentClasses
                ?.filter((x, i) => i < sectionStudentClasses?.length - 1)
                ?.map((section) => {
                    const adminApproved =
                        props.user?.profile?.admin_approved_classes
                    const filteredOptions =
                        adminApproved?.length > 0
                            ? section?.options?.filter((opt) =>
                                  adminApproved.includes(opt)
                              )
                            : section?.options
                    return {
                        ...section,
                        data: filteredOptions?.map((x) => ({
                            value: x,
                            label: x,
                        })),
                    }
                })
                ?.filter((section) => section.data?.length > 0), // Remove empty sections
            defaultValue:
                props.user?.profile?.relevant_classes ??
                (props.user?.profile?.admin_approved_classes?.length > 0
                    ? []
                    : studentClasses?.filter(
                          (x, i) => i < studentClasses?.length - 1
                      )),
            validationRules: { ...validation.required() },
        },
        {
            label: "Indique tes informations de facturation",
            type: "text.multiple",
            inputs: [
                {
                    name: "num_SIRET",
                    placeholder: "Numéro SIRET",
                    defaultValue: props.user?.profile?.num_SIRET,
                    hidden: !isAutoEntrepreneur,
                },
                {
                    name: "num_IBAN",
                    placeholder: "Numéro IBAN",
                    defaultValue: props.user?.profile?.num_IBAN,
                    validationRules: {
                        ...validation.required(),
                        ...validation.iban(),
                    },
                },
            ],
            defaultValue: "",
            topComponent: (
                <SwitchComponent
                    value={isAutoEntrepreneur}
                    setValue={setIsAutoEntrepreneur}
                    label="J'ai déjà le statut auto-entrepreneur"
                />
            ),
        },
        {
            label: "Personnalise ton profil",
            type: "custom",
            inputs: [
                {
                    name: "study_field",
                    defaultValue: props.user?.profile?.study_field ?? null,
                    validationRules: { ...validation.required() },
                },
                {
                    name: "study_institution",
                    defaultValue:
                        props.user?.profile?.study_institution ?? null,
                    validationRules: { ...validation.required() },
                },
                {
                    name: "tutor_skill_set_title_id",
                    defaultValue: props.user?.title_skill_set_id ?? 101,
                    validationRules: { ...validation.required() },
                },
            ],
            component: TutorProfilePersonalizer,
        },
        {
            label: "Lis et approuve la charte d'engagement du tuteur",
            name: "charter_opt_in",
            showNextButton: charterOptIn,
            type: "custom",
            component: () =>
                IS_WEB ? (
                    <iframe
                        style={{ flex: 1 }}
                        src={"https://masteur.com/charte-dengagement"}
                        frameBorder="0"
                    />
                ) : (
                    <WebView
                        style={{ flex: 1 }}
                        source={{
                            uri: "https://masteur.com/charte-dengagement",
                        }}
                    />
                ),
            bottomComponent: (
                <Checkbox.Item
                    label="J'accepte la charte d'engagement du tuteur"
                    position={"leading"}
                    onPress={() => {
                        setCharterOptIn(!charterOptIn)
                    }}
                    color={theme.colors.primary}
                    labelStyle={{
                        fontSize: 14,
                        textAlign: "left",
                    }}
                    style={{
                        marginHorizontal: 5,
                        marginTop: 5,
                    }}
                    status={charterOptIn ? "checked" : "indeterminate"}
                />
            ),
            defaultValue: props.user?.profile?.charter_opt_in ?? false,
            validationRules: {
                ...validation.required(),
                ...validation.customCondition(
                    () => charterOptIn,
                    "Tu dois accepter la charte d'engagement du tuteur"
                ),
            },
        },
    ]

    const onSubmit = (data) => {
        if (props.submitPersonalInfosRequest) {
            return
        }

        const { availability, ...rest } = data

        props.apiRequest(
            tutorsService.submitCompleteProfile,
            {},
            {
                ...rest,
                availability_slots: availability?.slots ?? [],
                availability_starting_date: availability?.startingDate ?? null,
                charter_opt_in: charterOptIn,
            }
        )
    }

    useApiStatus(
        tutorsService.submitCompleteProfile, // api service
        null, // success message on toast (string or null)
        false, // error message on toast (true) or keep it in redux state (false)
        () => {
            const congrats = [
                {
                    congratsType: congratsTypes.GENERIC,
                    congratsData: {
                        title: "Effectué",
                        text: "Tes informations ont bien été enregistrées",
                    },
                },
            ]
            props.putCongrats(congrats)
            navigation.replace("congrats")
        }
    )

    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <ScreenSkeleton isLoading={!isUserLoaded}>
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData?.filter((x) => !x.is_masked)}
                    champion={null}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    defaultValues={defaultValues}
                    onSubmit={onSubmit}
                    submitted={props.request}
                    previousRouteName={"menu"}
                    submittedMessage=""
                    serverError={props.error}
                    formId="complete-tutor-profile"
                    entryId={props.user?.id}
                />
            </ScreenSkeleton>
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        success: state.api.submitCompleteProfile?.success,
        request: state.api.submitCompleteProfile?.request,
        error: state.api.submitCompleteProfile?.error,
        schoolSubjects: state.data.staticData?.school_subjects,
        user: state.data.currentUser.object,
        // Read for the engaged half of the semaine-type, which the availability
        // step displays but never posts — the two rules above still count it.
        weekView: state.data.tutorAvailability?.weekView,
        putCongrats,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CompleteTutorProfileStepper)
